import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import RoleGuard from "@/components/auth/RoleGuard";
import SubmitOrderTab from "@/features/purchase-orders/components/SubmitOrderTab";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { Toaster } from "@/components/ui/toaster";
import { PageErrorBoundary } from "@/components/fallbacks";
import { PageLoader } from "@/components/loading/PageLoader";
import { useOrdersWorkflow } from "@/features/purchase-orders/hooks/useOrdersWorkflow";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { BackToTopPill } from "@/components/BackToTopPill";
import { EditingPill } from "@/features/purchase-orders/components/EditingPill";
import { getScrollContainer } from "@/lib/scroll-container";
import { safePurchaseOrderListReturnTo } from "@/features/purchase-orders/utils/list-state";

const OrderEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const ordersReturnTo = safePurchaseOrderListReturnTo(
    (routeLocation.state as { ordersReturnTo?: unknown } | null)?.ordersReturnTo,
  );
  const {
    orders,
    ordersLoading,
    products,
    inventoryLoading,
    handleCreateOrder,
    handleCreateDraftOrder,
    handleUpdateDraftOrder,
    handleSubmitDraft,
  } = useOrdersWorkflow(id);
  const { isLoading: locationsLoading } = useLocations();

  const order = useMemo(
    () => orders.find((o) => o.id === id) ?? null,
    [orders, id]
  );

  // Push PO identity + amber Editing pill into the centred TopHeader slot.
  const headerTitle = useMemo(() => {
    if (!order) return null;
    return (
      <span className="inline-flex items-center gap-2">
        <span>{order.po_number}</span>
        <EditingPill />
      </span>
    );
  }, [order]);
  const headerSubtitle = useMemo(() => {
    if (!order) return null;
    return order.order_type === "transfer" ? "Transfer Order" : (order.supplier_name || "");
  }, [order]);
  usePageTitle(headerTitle, headerSubtitle, { label: "Back to Orders", to: ordersReturnTo });

  // Always start the edit screen at the top. The actual scroll surface is
  // <SidebarInset>, not window — getScrollContainer finds it via its
  // data-sidebar attribute. Scrolling window was a silent no-op.
  useEffect(() => {
    getScrollContainer(null)?.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  const handleCancelEdit = () => {
    navigate(`/orders/${id}`, { state: { ordersReturnTo } });
  };

  const handleUpdateDraftForOrder = async (
    orderId: string,
    supplierName: string,
    supplierId: string | null,
    orderQuantities: Record<string, number>,
    title?: string,
    description?: string,
    etaDate?: string,
    unitCosts?: Record<string, number>
  ) => {
    await handleUpdateDraftOrder(
      orderId,
      supplierName,
      supplierId,
      orderQuantities,
      title,
      description,
      etaDate,
      order?.location_id,
      unitCosts
    );
  };

  const handleSubmitDraftForOrder = async (
    orderId: string,
    supplierName: string,
    supplierId: string | null
  ) => {
    await handleSubmitDraft(orderId, supplierName, supplierId, order?.location_id);
  };

  if (ordersLoading || inventoryLoading || locationsLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="purchaseOrders" />
        </div>
      </RoleGuard>
    );
  }

  if (!order) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              onClick={() => navigate(ordersReturnTo)}
              className="mb-4 -ml-2 flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Orders
            </Button>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-8 text-center">
              <h2 className="text-xl font-semibold text-amber-700 mb-2">
                Order Not Found
              </h2>
              <p className="text-amber-600">
                The purchase order you are trying to edit does not exist or has
                been removed.
              </p>
            </div>
          </main>
        </div>
      </RoleGuard>
    );
  }

  if (!inventoryLoading && products.length === 0) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col items-center justify-center p-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 max-w-md">
            <h2 className="text-xl font-semibold text-amber-700 mb-2">
              No Inventory Available
            </h2>
            <p className="text-amber-700">
              Add inventory items before editing purchase orders.
            </p>
          </div>
        </div>
      </RoleGuard>
    );
  }

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          {/* "Back to Orders" lives in the TopHeader (typewriter button). */}
          <SubmitOrderTab
            products={products}
            loading={inventoryLoading}
            onCreateOrder={handleCreateOrder}
            onCreateDraftOrder={handleCreateDraftOrder}
            onUpdateDraftOrder={handleUpdateDraftForOrder}
            onSubmitDraftOrder={handleSubmitDraftForOrder}
            editingDraft={order}
            onCancelEdit={handleCancelEdit}
          />
        </main>

        <BackToTopPill />

        <Toaster />
      </div>
    </RoleGuard>
  );
};

export default function OrderEditWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <OrderEdit />
    </PageErrorBoundary>
  );
}
